
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { UTILS } from "../config/API";
import app from "../assets/icons/app.png";
import appConfig, { getPortalKey } from "../config";

const Sidebar = ({ onItemSelect }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [openMenus, setOpenMenus] = useState(new Set());
  const [menuData, setMenuData] = useState(null);
  const [images, setImages] = useState({});
  const [accountType, setAccountType] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [appName, setAppName] = useState("");
  const [selectedSubmenu, setSelectedSubmenu] = useState("");
  const [hasFetchedMenuData, setHasFetchedMenuData] = useState(false); // Track if fetch was attempted
  const maxRetries = 5;
  const [config, setConfig] = useState({});

  // Function to check if expiration date has passed
  const isExpirationDatePassed = useCallback((expirationDate) => {
    if (!expirationDate) return true; // If no date, consider it expired

    try {
      const [month, day, year] = expirationDate.split('/').map(Number);
      const expiration = new Date(year, month - 1, day); // month is 0-indexed
      const currentDate = new Date();

      return currentDate > expiration;
    } catch (error) {
      console.error("Error parsing expiration date:", error);
      return true; // If there's an error parsing, consider it expired
    }
  }, []);

  // Initialize config and settings
  useEffect(() => {
    const key = getPortalKey();
    setConfig(appConfig[key] || appConfig.portal);
  }, []);

  // Initialize config and app name
  useEffect(() => {
    const key = getPortalKey();
    const config = appConfig[key] || appConfig.portal;
    if (config) {
      setAppName(config.name);
    }
  }, []);

  // Update selected submenu based on current URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const currentTab = urlParams.get('tab');

    if (currentTab) {
      setSelectedSubmenu(currentTab);
    } else {
      const path = location.pathname;
      setSelectedSubmenu(path.split('/').pop() || "");
    }
  }, [location]);

  const toggleMenu = useCallback((menuName) => {
    setOpenMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuName)) {
        newSet.delete(menuName);
      } else {
        newSet.add(menuName);
      }
      return newSet;
    });
  }, []);

  const openAllMenus = useCallback((items) => {
    const menuNames = items.map(item => item.name);
    setOpenMenus(new Set(menuNames));
  }, []);

  const handleHomeClick = useCallback(() => {
    navigate("/home");
    onItemSelect?.();
    setSelectedSubmenu("");
  }, [navigate, onItemSelect]);

  const handleSubmenuClick = useCallback((submenu, link) => {
    const isUrl = /^https?:\/\//.test(link);

    setSelectedSubmenu(submenu);

    if (isUrl) {
      window.open(link, submenu);
    } else {
      const hasQuery = link.includes('?');
      const newUrl = hasQuery
        ? `${link}&tab=${encodeURIComponent(submenu)}`
        : `${link}?tab=${encodeURIComponent(submenu)}`;

      const currentUrl = window.location.href;
      const isSameTab = currentUrl.includes(`tab=${encodeURIComponent(submenu)}`) ||
        (currentUrl.endsWith(link) && !currentUrl.includes('tab=') && !submenu);

      if (isSameTab) {
        window.location.reload();
      } else if (link) {
        navigate(newUrl);
      }
    }

    onItemSelect?.();
  }, [navigate, onItemSelect]);

  const xmlToJson = useCallback((xml) => {
    const result = {};
    const processNode = (node, obj) => {
      if (node.nodeType === 1) {
        const nodeName = node.nodeName;
        const childObj = {};

        if (node.attributes.length > 0) {
          childObj["@attributes"] = {};
          for (let i = 0; i < node.attributes.length; i++) {
            const attr = node.attributes[i];
            childObj["@attributes"][attr.nodeName] = attr.nodeValue;
          }
        }

        for (let i = 0; i < node.childNodes.length; i++) {
          processNode(node.childNodes[i], childObj);
        }

        if (obj[nodeName]) {
          if (!Array.isArray(obj[nodeName])) obj[nodeName] = [obj[nodeName]];
          obj[nodeName].push(childObj);
        } else {
          obj[nodeName] = childObj;
        }
      } else if (node.nodeType === 3 && node.nodeValue.trim() !== "") {
        obj["#text"] = node.nodeValue.trim();
      }
    };

    processNode(xml.documentElement, result);
    return result;
  }, []);

  const fetchMenuData = useCallback(async () => {
    if (isLoading || hasFetchedMenuData) return; // Prevent multiple calls
    setIsLoading(true);

    try {
      const response = await fetch(UTILS, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json"
        },
        body: new URLSearchParams({ action: "menu" }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const xmlText = await response.text();
      const xmlDoc = new DOMParser().parseFromString(xmlText, "application/xml");
      const jsonData = xmlToJson(xmlDoc);

      const storedMenuData = sessionStorage.getItem("menuData");
      const stringifiedNewData = JSON.stringify(jsonData, null, 2);

      console.log("stringifiedNewData", stringifiedNewData);

      if (storedMenuData !== stringifiedNewData) {
        sessionStorage.setItem("menuData", stringifiedNewData);
        setMenuData(jsonData);
      }

      setHasFetchedMenuData(true); // Mark as fetched
    } catch (error) {
      console.error("Failed to fetch menu data:", error);
      setHasFetchedMenuData(true); // Mark as fetched even on error to prevent retries
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, xmlToJson, hasFetchedMenuData]); // Add hasFetchedMenuData to dependencies

  // Helper function to check if showMenu=false exists in args
  const shouldHideMenu = useCallback((args) => {
    if (!args) return false;
    // Split by comma and check each parameter
    const params = args.split(',').map(param => param.trim());
    return params.includes("showMenu=false");
  }, []);

  // Helper function to extract icon path from args (first part before comma)
  const extractIconPath = useCallback((args) => {
    if (!args) return "";
    // Get the first part before comma (the icon path)
    const parts = args.split(',').map(part => part.trim());
    return parts[0] || "";
  }, []);

  const transformMenuData = useCallback((data) => {
    if (!data?.fcnode?.fcnode) return [];

    const currentAccountType = accountType || sessionStorage.getItem("accountType");

    const menuNodes = Array.isArray(data.fcnode.fcnode)
      ? data.fcnode.fcnode
      : [data.fcnode.fcnode];

    return menuNodes
      .map((menu) => {
        // Check if menu is null or doesn't have attributes
        if (!menu || !menu["@attributes"]) return null;

        // Check if menu should be hidden based on args
        const menuArgs = menu["@attributes"].args || "";
        if (shouldHideMenu(menuArgs)) {
          return null; // Hide this menu item
        }

        let submenus = [];

        if (menu.fcnode) {
          const submenuNodes = Array.isArray(menu.fcnode)
            ? menu.fcnode
            : [menu.fcnode];

          submenus = submenuNodes
            .map((submenu) => {
              // Check if submenu is null or doesn't have attributes
              if (!submenu || !submenu["@attributes"]) return null;

              // Check if submenu should be hidden based on args
              const submenuArgs = submenu["@attributes"].args || "";
              if (shouldHideMenu(submenuArgs)) {
                return null; // Hide this submenu item
              }

              return {
                name: submenu["@attributes"].name,
                link: submenu["@attributes"].link,
                tooltip: submenu["@attributes"].tooltip || submenu["@attributes"].name,
                winname: submenu["@attributes"].winname || submenu["@attributes"].name
              };
            })
            .filter(submenu => submenu !== null); // Remove null submenus

          if (config.name === "Prairie Band Potawatomi Nation") {
            const taxPayerType = localStorage.getItem("selectedTaxPayerType");
            const businessLicenseType = localStorage.getItem("businessLicenseType");
            const businessLicenseStatus = localStorage.getItem("businessLicenseStatus");
            const expirationDate = localStorage.getItem("businessLicenseExpirationDate");
            console.log("Business License Expiration Date from localStorage:", expirationDate);

            // Convert comma-separated string to array and trim each item
            const licenseTypes = businessLicenseType ? businessLicenseType.split(',').map(type => type.trim()) : [];


            if (licenseTypes.length === 0 && menu["@attributes"].name === "Tax Return") {

              return null; // Return null to filter out this menu
            }
            if (expirationDate === "" && menu["@attributes"].name === "Tax Return") {

              return null;
            }
            if (businessLicenseStatus !== "Approved" && menu["@attributes"].name === "Tax Return") {

              return null;
            }


            if (expirationDate && menu["@attributes"].name === "Tax Return") {
              if (isExpirationDatePassed(expirationDate)) {
                console.log("Business license has expired. Hiding Tax Return menu.");
                return null;
              }
            }

            if (taxPayerType === "Retailer") {
              submenus = submenus.filter(submenu =>
                submenu.name !== "Distributor – Stamp Inventory" &&
                submenu.name !== "Distributor - Unstamped Packs" &&
                submenu.name !== "Distributor - Off-Reservation Packs" &&
                submenu.name !== "Distributor – NPM Sales" &&
                submenu.name !== "Distributor – Stamps, Packs" &&
                submenu.name !== "Distributor Fuel" &&
                submenu.name !== "Distributor – Packs"
              );
            }

            if (taxPayerType === "Distributor") {
              submenus = submenus.filter(submenu =>
                submenu.name !== "Retailer - Packs" &&
                submenu.name !== "Retailer - Sales Tax" &&
                submenu.name !== "Retailer – Annual Packs Report" &&
                submenu.name !== "Retailer Fuel" &&
                submenu.name !== "Retailer Sales Tax - Casino"
              );
            }

            if (!licenseTypes.includes("Fuel")) {
              submenus = submenus.filter(submenu =>
                submenu.name !== "Retailer Fuel" &&
                submenu.name !== "Distributor Fuel"
              );
            }
            if (!licenseTypes.includes("Casino")) {
              submenus = submenus.filter(submenu => submenu.name !== "Retailer Sales Tax - Casino");
            }
            // else {
            //   submenus = submenus.filter(submenu => submenu.name !== "Retailer - Sales Tax");
            // }

            if (!licenseTypes.includes("Tobacco")) {
              submenus = submenus.filter(submenu =>
                submenu.name !== "Distributor – Stamp Inventory" &&
                submenu.name !== "Distributor - Unstamped Packs" &&
                submenu.name !== "Distributor - Off-Reservation Packs" &&
                submenu.name !== "Distributor – NPM Sales" &&
                submenu.name !== "Distributor – Stamps, Packs" &&
                submenu.name !== "Distributor – Packs" &&
                submenu.name !== "Retailer - Packs" &&
                submenu.name !== "Retailer – Annual Packs Report"

              );
            }
          }
        }

        return {
          name: menu["@attributes"].name,
          icon: extractIconPath(menu["@attributes"].args),
          tooltip: menu["@attributes"].tooltip || menu["@attributes"].name,
          submenus,
        };
      })
      .filter(menu => menu !== null) // Remove null menus
      .map((menu) => {
        const isMyForms =
          ["TERO", "Klamath Tribes"].includes(appName) &&
          menu.name === "My Forms" &&
          ["Staff", "Contractor", "TERO Worker"].includes(currentAccountType);

        if (isMyForms) {
          let allowedMenus = [];
          console.log("currentAccountType", currentAccountType);

          if (currentAccountType === "Staff") {
            allowedMenus = ["Site Checks", "Items Received"];
          } else if (currentAccountType === "Contractor") {
            allowedMenus = ["Compliance Plan", "Compliance Plan Adjustments", "Employment Verification", "Employee Termination", "TERO Worker Evaluation"];
          } else if (currentAccountType === "TERO Worker") {
            allowedMenus = ["Request for Services", "Company Evaluation", "New Intake"];
          }

          menu.submenus = menu.submenus.filter((submenu) =>
            allowedMenus.includes(submenu.name)
          );
        }

        const isTeroProfile = appName === "TERO" && menu.name === "About Me";
        if (isTeroProfile && Array.isArray(menu.submenus)) {
          // Show "Check In" only for TERO Workers
          if (currentAccountType !== "TERO Worker") {
            menu.submenus = menu.submenus.filter((submenu) => submenu.name !== "Check In");
          }
        }

        return menu;
      })
      .filter((menu) => {
        const isMyForms =
          appName === "TERO" &&
          menu.name === "My Forms" &&
          ["Staff", "Contractor", "TERO Worker"].includes(currentAccountType);

        if (isMyForms) {
          return menu.submenus.length > 0;
        }

        return true;
      });
  }, [accountType, appName, isExpirationDatePassed, shouldHideMenu, config, extractIconPath]);


  // Add this useEffect to your Sidebar component
  useEffect(() => {
    const handleStorageChange = () => {
      // Clear menu data and refetch when businessLicenseType changes
      sessionStorage.removeItem("menuData");
      setHasFetchedMenuData(false);
      setMenuData(null);
    };

    // Listen for storage events
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom event (for same-tab updates)
    window.addEventListener('localStorageChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleStorageChange);
    };
  }, []);

  // Fetch account type
  useEffect(() => {
    const storedAccountType = sessionStorage.getItem("accountType");
    if (storedAccountType) {
      setAccountType(storedAccountType);
    } else if (retryCount < maxRetries) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [retryCount]);

  // Fetch menu data only once when component mounts
  useEffect(() => {
    const storedMenuData = sessionStorage.getItem("menuData");
    if (storedMenuData) {
      try {
        const parsedData = JSON.parse(storedMenuData);
        setMenuData(parsedData);
        setHasFetchedMenuData(true); // Mark as fetched since we have data
      } catch (e) {
        sessionStorage.removeItem("menuData");
        // Only fetch if we haven't already attempted to fetch
        if (!hasFetchedMenuData) {
          fetchMenuData();
        }
      }
    } else {
      // Only fetch if we haven't already attempted to fetch
      if (!hasFetchedMenuData) {
        fetchMenuData();
      }
    }
  }, [fetchMenuData, hasFetchedMenuData]); // Add hasFetchedMenuData to dependencies

  const menuItems = useMemo(() => {
    if (!menuData) return [];
    const items = transformMenuData(menuData);
    if (items.length > 0 && openMenus.size === 0) {
      openAllMenus(items);
    }
    return items;
  }, [menuData, transformMenuData, openMenus.size, openAllMenus]);

  useEffect(() => {
    const loadImages = async () => {
      if (!menuItems.length) return;

      const newImages = {};
      let needsUpdate = false;

      for (const item of menuItems) {
        if (!images[item.icon]) {
          try {
            const image = await import(`../assets/icons/${item.icon}`);
            newImages[item.icon] = image.default;
            needsUpdate = true;
          } catch (error) {
            console.error(`Failed to load image: ${item.icon}`, error);
            newImages[item.icon] = app;
            needsUpdate = true;
          }
        }
      }

      if (needsUpdate) {
        setImages(prev => ({ ...prev, ...newImages }));
      }
    };

    loadImages();
  }, [menuItems, images]);

  if (!appName || !accountType || !menuData) {
    return (
      <div className="flex items-center justify-center h-64 w-64 bg-white">
        <div className="relative inline-flex">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-4 h-4 bg-primary rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col w-64 bg-white text-black h-screen sticky top-0
        ${appName === "Colorado River Indian Tribes" ? "lg:pt-6" : "lg:pt-16"} px-4 pb-4`}
    >
      <button
        onClick={handleHomeClick}
        className="text-lg font-bold mb-4 hover:underline text-left mt-5"
      >
        {appName === "TERO" ? "My TERO" : appName === "Colorado River Indian Tribes" ? "Taxpayer Portal Home" : appName}
      </button>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <ul className="space-y-2 pb-2">
          {menuItems.map((item) => (
            <li key={item.name}>
              <div
                className="flex justify-between items-center hover:bg-primary hover:text-white p-2 rounded cursor-pointer group"
                onClick={() => toggleMenu(item.name)}
                title={item.tooltip}
              >
                <div className="flex items-center">
                  <img
                    src={images[item.icon] || app}
                    alt={item.name}
                    className="w-5 h-5 mr-2 group-hover:brightness-0 group-hover:invert"
                  />
                  <span className="font-bold">{item.name}</span>
                </div>
                <svg
                  className={`w-4 h-4 transition-transform duration-700 transform ${openMenus.has(item.name) ? 'rotate-90' : 'rotate-0'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>

              <div
                className={`transition-all duration-700 ease-in-out ${openMenus.has(item.name)
                  ? 'max-h-[60vh] overflow-y-auto'
                  : 'max-h-0 overflow-hidden'
                  }`}
              >
                <ul className="pl-4 mt-2 space-y-1 pr-1">
                  {item.submenus.map((submenu) => {
                    const isSelected = selectedSubmenu === (submenu.winname || submenu.name);
                    return (
                      <li
                        key={submenu.name}
                        className={`p-2 rounded cursor-pointer ${isSelected
                          ? 'bg-primary text-white font-medium'
                          : 'text-gray-600 hover:bg-primary hover:text-white'
                          }`}
                        onClick={() => handleSubmenuClick(submenu.winname || submenu.name, submenu.link)}
                        title={submenu.tooltip}
                      >
                        {submenu.name}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;